import { useState, useEffect } from 'react';

type UserGoal = 'first-time' | 'failed' | 'exam-soon' | null;

/**
 * Hook to read user's onboarding goal.
 * - For unauthenticated users: reads from localStorage ('selectedGoal')
 * - For authenticated users: reads from Supabase user_profiles.user_goal
 * Returns { goal, loading }
 */
export function useUserGoal(): { goal: UserGoal; loading: boolean } {
  const [goal, setGoal] = useState<UserGoal>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGoal = async () => {
      try {
        // Try to get from localStorage (unauthenticated flow)
        const stored = localStorage.getItem('selectedGoal');
        if (stored === 'first-time' || stored === 'failed' || stored === 'exam-soon') {
          setGoal(stored);
          setLoading(false);
          return;
        }

        // If no localStorage, goal will be null (defaults to generic dashboard)
        setGoal(null);
      } catch {
        setGoal(null);
      } finally {
        setLoading(false);
      }
    };

    fetchGoal();
  }, []);

  return { goal, loading };
}

/**
 * Get goal from localStorage synchronously (no hook)
 */
export function getStoredGoal(): UserGoal {
  const stored = localStorage.getItem('selectedGoal');
  if (stored === 'first-time' || stored === 'failed' || stored === 'exam-soon') {
    return stored;
  }
  return null;
}

/**
 * Store goal to localStorage
 */
export function setStoredGoal(goal: UserGoal): void {
  if (goal === null) {
    localStorage.removeItem('selectedGoal');
  } else {
    localStorage.setItem('selectedGoal', goal);
  }
}
